import React, { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useRadiusSize,
  useSpaceSize,
  useTheme,
} from "arkh";
import { TeamScheduleSectionHeaderStatusDot } from "./TeamScheduleSectionHeaderStatusDot";
import Animated, {
  Extrapolation,
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { LayoutChangeEvent, Platform } from "react-native";
import * as Haptics from "expo-haptics";
import { StatusCompact } from "./StatusCompact";
import { TeamScheduleSectionHeaderSummaryOccasions_teamSchedule$key } from "./__generated__/TeamScheduleSectionHeaderSummaryOccasions_teamSchedule.graphql";
import { TeamScheduleSectionHeaderSummaryHeight } from "./TeamScheduleSectionHeader";

export function TeamScheduleSectionHeaderSummaryOccasions(props: {
  occasions: TeamScheduleSectionHeaderSummaryOccasions_teamSchedule$key;
  title: string;
  onPress(): void;
  stickyIndex?: SharedValue<number>;
  index?: number;
  isFree?: boolean;
}) {
  const occasions = useFragment(
    graphql`
      fragment TeamScheduleSectionHeaderSummaryOccasions_teamSchedule on RoutineOccasion
      @relay(plural: true) {
        status(calculateNoTasks: true, calculateNotCompletedStarted: true)
        ...TeamScheduleSectionHeaderStatusDot_routineOccasion
      }
    `,
    props.occasions
  );

  const renderStatus = useCallback(
    (occasion: (typeof occasions)[number], index: number) => (
      <Box spaceLeft="xxsmall" spaceBottom="xxsmall" key={index}>
        <TeamScheduleSectionHeaderStatusDot occasion={occasion} />
      </Box>
    ),
    []
  );

  const cr = useRadiusSize("medium");
  const horizontal = useSpaceSize("medium");
  const theme = useTheme();
  const style = useAnimatedStyle(() => {
    const cornerRadios =
      props.index !== undefined && props.stickyIndex
        ? interpolate(
            props.stickyIndex.value,
            [props.index - 0.01, props.index],
            [0, cr],
            Extrapolation.CLAMP
          )
        : props.isFree
          ? cr
          : 0;
    return {
      backgroundColor: theme.surface,
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      paddingHorizontal: horizontal,
      borderRadius: cr,
      borderBottomLeftRadius: cornerRadios,
      borderBottomRightRadius: cornerRadios,
    };
  });

  const layout = useLayoutContext();
  const [width, setWidth] = useState(0);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width > 1) {
      setWidth(event.nativeEvent.layout.width);
    }
  }, []);
  const compactMode = useMemo(() => {
    return (
      occasions.length * (layout.circle.dot + layout.space.xxsmall) >
      width - layout.space.medium
    );
  }, [
    layout.circle.dot,
    layout.space.medium,
    layout.space.xxsmall,
    occasions.length,
    width,
  ]);

  return (
    <TouchableBox
      onPressIn={() => {
        if (Platform.OS === "ios") {
          Haptics.selectionAsync();
        }
      }}
      onPress={props.onPress}
      style={{
        height: TeamScheduleSectionHeaderSummaryHeight,
        overflow: "hidden",
        zIndex: 9,
      }}
      spaceTop="small"
    >
      <Animated.View style={style}>
        <AHText type="subtitle" color="darkText">
          {props.title}
        </AHText>
        <Box style={{ flex: 1 }} onLayout={onLayout}>
          {compactMode ? (
            <StatusCompact occasions={occasions} />
          ) : (
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                flex: 1,
              }}
            >
              {occasions.map(renderStatus)}
            </Box>
          )}
        </Box>
      </Animated.View>
    </TouchableBox>
  );
}
